import { useEffect, useState, useCallback } from 'react'
import { useSpecialtyConfig } from '@/lib/hooks/useSpecialtyConfig'

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:3001'

function authH() {
  return { Authorization: `Bearer ${localStorage.getItem('velink_token')}` }
}

async function adminFetch<T>(path: string): Promise<T> {
  const res = await fetch(`${API}/api/admin${path}`, { headers: authH() })
  if (!res.ok) throw new Error('Erreur')
  return res.json()
}

async function adminPost<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${API}/api/admin${path}`, {
    method: 'POST',
    headers: { ...authH(), 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error((data as { message?: string }).message ?? 'Erreur')
  return data as T
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function euros(cents: number) {
  return (cents / 100).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface Company {
  id: string; name: string; email: string; siret: string | null
  phone: string | null; isSuspended: boolean; createdAt: string
  _count: { missions: number }
}

interface Provider {
  id: string; firstName: string; lastName: string; email: string
  phone: string | null; rating: number; ratingCount: number
  specialties: string[]; isSuspended: boolean; isVerified: boolean
  createdAt: string; _count: { assignments: number }
}

interface Payment {
  id: string; amount: number; platformFee: number; providerNet: number
  method: string; status: string; capturedAt: string | null; createdAt: string
  mission: {
    id: string; title: string
    company: { name: string }
    assignments: Array<{ provider: { firstName: string; lastName: string } }>
  }
}

interface TrainingSession {
  id: string; title: string; type: string; status: string
  startDate: string; endDate: string; durationH: number
  city: string | null; isRemote: boolean; createdAt: string
  company: { name: string }
  _count: { trainees: number }
}

interface Review {
  id: string; rating: number; punctuality: number; behavior: number
  comment: string | null; createdAt: string
  provider: { firstName: string; lastName: string }
  mission: { title: string; company: { name: string } }
}

// ─── Config ───────────────────────────────────────────────────────────────────

const PAYMENT_STATUS: Record<string, { label: string; color: string }> = {
  PENDING:    { label: 'En attente',  color: 'bg-white/[0.06] border-white/[0.12] text-text-muted' },
  AUTHORIZED: { label: 'Autorisé',   color: 'bg-accent/10 border-accent/25 text-accent' },
  CAPTURED:   { label: 'Capturé',    color: 'bg-success/10 border-success/25 text-success' },
  RELEASED:   { label: 'Libéré',     color: 'bg-success/10 border-success/25 text-success' },
  REFUNDED:   { label: 'Remboursé',  color: 'bg-warning/10 border-warning/25 text-warning' },
  FAILED:     { label: 'Échoué',     color: 'bg-danger/10 border-danger/25 text-danger' },
}

const TRAINING_STATUS: Record<string, { label: string; color: string }> = {
  PLANNED:     { label: 'Planifiée', color: 'bg-accent/10 border-accent/25 text-accent' },
  IN_PROGRESS: { label: 'En cours',  color: 'bg-warning/10 border-warning/25 text-warning' },
  COMPLETED:   { label: 'Terminée',  color: 'bg-success/10 border-success/25 text-success' },
  CANCELLED:   { label: 'Annulée',   color: 'bg-white/[0.06] border-white/[0.12] text-text-muted' },
}

const TRAINING_TYPE: Record<string, string> = {
  INITIALE: 'Initiale', RECYCLAGE: 'Recyclage', HABILITATION: 'Habilitation',
  SENSIBILISATION: 'Sensibilisation', AUTRE: 'Autre',
}

// ─── Maintenance (migrations Prisma, santé DB) ────────────────────────────────

interface DbStatus {
  db: { name: string; user: string; version: string; sizeMb: number }
  privileges: { schemaCreate: boolean }
  migrations: {
    dir: string | null
    applied: { name: string; finishedAt: string | null; onDisk: boolean }[]
    pending: string[]
    failed: { name: string; startedAt: string; logs: string | null }[]
    running: boolean
  }
  counts: Record<string, number>
}

interface PrismaRunResult { ok: boolean; command: string; output: string; durationMs: number }

function MaintenancePanel({ onPendingChange }: { onPendingChange: (pending: string[]) => void }) {
  const [status, setStatus] = useState<DbStatus | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState<string | null>(null)
  const [log, setLog] = useState<PrismaRunResult | null>(null)
  const [showApplied, setShowApplied] = useState(false)

  const load = useCallback(async () => {
    setError('')
    try {
      const s = await adminFetch<DbStatus>('/db/status')
      setStatus(s)
      onPendingChange(s.migrations.pending)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur')
    }
  }, [onPendingChange])

  useEffect(() => { load() }, [load])

  async function run(label: string, action: () => Promise<PrismaRunResult>) {
    setBusy(label)
    setLog(null)
    try {
      setLog(await action())
    } catch (e) {
      setLog({ ok: false, command: label, output: e instanceof Error ? e.message : 'Erreur', durationMs: 0 })
    } finally {
      setBusy(null)
      load()
    }
  }

  if (error) {
    return <div className="px-4 py-3 rounded-xl bg-danger/10 border border-danger/25 text-danger text-sm">{error}</div>
  }
  if (!status) return <div className="text-text-muted animate-pulse py-10 text-center text-sm">Diagnostic en cours…</div>

  const { db, privileges, migrations, counts } = status
  const pendingCount = migrations.pending.length
  const failedCount = migrations.failed.length

  return (
    <div className="space-y-5">
      {/* Cartes d'état */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        <div className="glass-card p-4">
          <p className="text-[11px] uppercase tracking-wider text-text-muted mb-2">Base</p>
          <p className="text-text-primary font-semibold">{db.name}</p>
          <p className="text-text-secondary text-xs mt-0.5">{db.version}</p>
          <p className="text-text-muted text-xs">utilisateur <span className="text-text-secondary">{db.user}</span> · {db.sizeMb} Mo</p>
        </div>
        <div className="glass-card p-4">
          <p className="text-[11px] uppercase tracking-wider text-text-muted mb-2">Droits</p>
          {privileges.schemaCreate ? (
            <p className="text-success font-semibold">✓ CREATE sur le schéma public</p>
          ) : (
            <>
              <p className="text-danger font-semibold">✗ Pas de droit CREATE sur public</p>
              <p className="text-text-muted text-xs mt-1">Les migrations qui créent des tables échoueront. À lancer en superuser :</p>
              <code className="block mt-1 text-[11px] text-text-secondary bg-white/[0.04] rounded-lg px-2 py-1 break-all">
                GRANT CREATE ON SCHEMA public TO {db.user};
              </code>
            </>
          )}
        </div>
        <div className="glass-card p-4">
          <p className="text-[11px] uppercase tracking-wider text-text-muted mb-2">Migrations</p>
          <p className={`font-semibold ${failedCount ? 'text-danger' : pendingCount ? 'text-warning' : 'text-success'}`}>
            {failedCount ? `${failedCount} en échec` : pendingCount ? `${pendingCount} en attente` : '✓ À jour'}
          </p>
          <p className="text-text-muted text-xs mt-0.5">{migrations.applied.length} appliquée{migrations.applied.length > 1 ? 's' : ''}</p>
          {!migrations.dir && <p className="text-danger text-xs mt-1">Dossier migrations introuvable côté API</p>}
        </div>
        <div className="glass-card p-4">
          <p className="text-[11px] uppercase tracking-wider text-text-muted mb-2">Volumétrie</p>
          <div className="grid grid-cols-2 gap-x-3 text-xs">
            {Object.entries(counts).map(([k, v]) => (
              <p key={k} className="text-text-secondary"><span className="text-text-primary font-semibold">{v}</span> {k}</p>
            ))}
          </div>
        </div>
      </div>

      {/* Migrations en échec */}
      {failedCount > 0 && (
        <div className="glass-card p-5 border-danger/30">
          <h3 className="text-sm font-semibold text-danger mb-1">Migration{failedCount > 1 ? 's' : ''} en échec</h3>
          <p className="text-text-muted text-xs mb-3">
            Prisma refuse d'appliquer de nouvelles migrations tant qu'un échec n'est pas résolu. Corrigez la cause,
            puis marquez la migration « annulée » pour la rejouer, ou « appliquée » si vous l'avez terminée à la main.
          </p>
          <div className="space-y-2">
            {migrations.failed.map(f => (
              <div key={f.name} className="rounded-xl border border-white/[0.08] p-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <code className="text-xs text-text-primary">{f.name}</code>
                  <div className="flex gap-2">
                    <button
                      disabled={busy !== null}
                      onClick={() => confirm(`Marquer ${f.name} comme annulée (elle sera rejouée) ?`) &&
                        run('resolve', () => adminPost<PrismaRunResult>(`/db/migrations/${f.name}/resolve`, { as: 'rolled-back' }))}
                      className="glass-btn px-3 py-1.5 text-xs rounded-lg"
                    >↩ Annulée</button>
                    <button
                      disabled={busy !== null}
                      onClick={() => confirm(`Marquer ${f.name} comme appliquée sans la rejouer ?`) &&
                        run('resolve', () => adminPost<PrismaRunResult>(`/db/migrations/${f.name}/resolve`, { as: 'applied' }))}
                      className="glass-btn px-3 py-1.5 text-xs rounded-lg"
                    >✓ Appliquée</button>
                  </div>
                </div>
                {f.logs && <pre className="mt-2 text-[11px] text-danger/80 whitespace-pre-wrap max-h-32 overflow-auto">{f.logs}</pre>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Migrations en attente + action */}
      <div className="glass-card p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div>
            <h3 className="text-sm font-semibold text-text-primary">Appliquer les migrations</h3>
            <p className="text-text-muted text-xs mt-0.5">
              Équivalent de <code>prisma migrate deploy</code> — lancé automatiquement à chaque déploiement, disponible ici en secours.
            </p>
          </div>
          <button
            disabled={busy !== null || migrations.running || failedCount > 0}
            onClick={() => run('migrate', () => adminPost<PrismaRunResult>('/db/migrate'))}
            className="glass-btn-accent px-4 py-2 text-sm rounded-xl disabled:opacity-50"
          >
            {busy === 'migrate' || migrations.running ? '⏳ Migration en cours…' : `⚡ Appliquer${pendingCount ? ` (${pendingCount})` : ''}`}
          </button>
        </div>
        {pendingCount === 0 ? (
          <p className="text-success text-xs">✓ Aucune migration en attente</p>
        ) : (
          <ul className="space-y-1">
            {migrations.pending.map(n => (
              <li key={n} className="flex items-center gap-2 text-xs">
                <span className="w-1.5 h-1.5 rounded-full bg-warning" />
                <code className="text-text-secondary">{n}</code>
              </li>
            ))}
          </ul>
        )}
        {log && (
          <div className={`mt-4 rounded-xl border p-3 ${log.ok ? 'border-success/30 bg-success/5' : 'border-danger/30 bg-danger/5'}`}>
            <p className={`text-xs font-semibold mb-1 ${log.ok ? 'text-success' : 'text-danger'}`}>
              {log.ok ? '✓' : '✗'} {log.command}{log.durationMs ? ` · ${(log.durationMs / 1000).toFixed(1)}s` : ''}
            </p>
            <pre className="text-[11px] text-text-secondary whitespace-pre-wrap max-h-64 overflow-auto">{log.output || '(aucune sortie)'}</pre>
          </div>
        )}
      </div>

      {/* Historique */}
      <div className="glass-card p-5">
        <button onClick={() => setShowApplied(v => !v)} className="text-sm font-semibold text-text-primary flex items-center gap-2">
          <span className="text-text-muted text-xs">{showApplied ? '▼' : '▶'}</span>
          Historique ({migrations.applied.length})
        </button>
        {showApplied && (
          <ul className="mt-3 space-y-1">
            {[...migrations.applied].reverse().map(m => (
              <li key={m.name} className="flex items-center justify-between gap-3 text-xs">
                <code className={m.onDisk ? 'text-text-secondary' : 'text-text-muted line-through'} title={m.onDisk ? '' : 'Absente du dépôt'}>{m.name}</code>
                <span className="text-text-muted">{m.finishedAt ? formatDate(m.finishedAt) : '—'}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

type Tab = 'entreprises' | 'prestataires' | 'paiements' | 'formations' | 'avis' | 'maintenance'

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function BaseDeDonneesPage() {
  const { byKey: specialtyByKey } = useSpecialtyConfig({ activeOnly: false })
  const [tab, setTab] = useState<Tab>('entreprises')
  const [search, setSearch] = useState('')

  const [companies, setCompanies] = useState<Company[]>([])
  const [providers, setProviders] = useState<Provider[]>([])
  const [payments, setPayments] = useState<Payment[]>([])
  const [sessions, setSessions] = useState<TrainingSession[]>([])
  const [reviews, setReviews] = useState<Review[]>([])

  const [totals, setTotals] = useState({ companies: 0, providers: 0, payments: 0, sessions: 0, reviews: 0 })
  const [loaded, setLoaded] = useState<Partial<Record<Tab, boolean>>>({})
  const [loading, setLoading] = useState(false)
  const [dbPending, setDbPending] = useState<string[]>([])

  // Détection au chargement : bandeau si des migrations attendent, quel que soit l'onglet
  useEffect(() => {
    adminFetch<DbStatus>('/db/status').then(s => setDbPending(s.migrations.pending)).catch(() => {})
  }, [])

  const loadTab = useCallback(async (t: Tab) => {
    if (t === 'maintenance' || loaded[t]) return
    setLoading(true)
    try {
      if (t === 'entreprises') {
        const d = await adminFetch<{ items: Company[]; total: number }>('/companies?limit=500')
        setCompanies(d.items); setTotals(prev => ({ ...prev, companies: d.total }))
      } else if (t === 'prestataires') {
        const d = await adminFetch<{ items: Provider[]; total: number }>('/providers?limit=500')
        setProviders(d.items); setTotals(prev => ({ ...prev, providers: d.total }))
      } else if (t === 'paiements') {
        const d = await adminFetch<{ items: Payment[]; total: number }>('/payments?limit=500')
        setPayments(d.items); setTotals(prev => ({ ...prev, payments: d.total }))
      } else if (t === 'formations') {
        const d = await adminFetch<{ items: TrainingSession[]; total: number }>('/training-sessions?limit=500')
        setSessions(d.items); setTotals(prev => ({ ...prev, sessions: d.total }))
      } else if (t === 'avis') {
        const d = await adminFetch<{ items: Review[]; total: number }>('/reviews?limit=500')
        setReviews(d.items); setTotals(prev => ({ ...prev, reviews: d.total }))
      }
      setLoaded(prev => ({ ...prev, [t]: true }))
    } catch { /* silencieux */ }
    finally { setLoading(false) }
  }, [loaded])

  useEffect(() => { loadTab('entreprises') }, [])

  function switchTab(t: Tab) {
    setTab(t); setSearch('')
    loadTab(t)
  }

  async function toggleSuspend(type: 'companies' | 'providers', id: string, current: boolean) {
    await fetch(`${API}/api/admin/${type}/${id}/suspend`, {
      method: 'PATCH',
      headers: { ...authH(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ suspend: !current }),
    })
    if (type === 'companies') setCompanies(prev => prev.map(c => c.id === id ? { ...c, isSuspended: !current } : c))
    else setProviders(prev => prev.map(p => p.id === id ? { ...p, isSuspended: !current } : p))
  }

  const q = search.toLowerCase()

  const filteredCompanies = companies.filter(c =>
    c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q)
  )
  const filteredProviders = providers.filter(p =>
    `${p.firstName} ${p.lastName}`.toLowerCase().includes(q) || p.email.toLowerCase().includes(q)
  )
  const filteredPayments = payments.filter(p =>
    p.mission.title.toLowerCase().includes(q) || p.mission.company.name.toLowerCase().includes(q)
  )
  const filteredSessions = sessions.filter(s =>
    s.title.toLowerCase().includes(q) || s.company.name.toLowerCase().includes(q)
  )
  const filteredReviews = reviews.filter(r =>
    `${r.provider.firstName} ${r.provider.lastName}`.toLowerCase().includes(q) ||
    r.mission.company.name.toLowerCase().includes(q)
  )

  const TABS: { id: Tab; label: string; emoji: string; count: number }[] = [
    { id: 'entreprises',  label: 'Entreprises',  emoji: '🏢', count: totals.companies },
    { id: 'prestataires', label: 'Prestataires', emoji: '👤', count: totals.providers },
    { id: 'paiements',    label: 'Paiements',    emoji: '💳', count: totals.payments },
    { id: 'formations',   label: 'Formations',   emoji: '🎓', count: totals.sessions },
    { id: 'avis',         label: 'Avis',         emoji: '⭐', count: totals.reviews },
    { id: 'maintenance',  label: 'Maintenance',  emoji: '🛠️', count: dbPending.length },
  ]

  return (
    <div>
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-text-primary">Base de données</h1>
          <p className="text-text-secondary mt-1 text-sm">Vue complète de toutes les tables</p>
        </div>
        <input
          type="text"
          placeholder="Rechercher…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="glass-input w-64 text-sm"
        />
      </div>

      {dbPending.length > 0 && tab !== 'maintenance' && (
        <button
          onClick={() => switchTab('maintenance')}
          className="w-full text-left mb-5 px-4 py-3 rounded-xl bg-warning/10 border border-warning/30 text-warning text-sm hover:bg-warning/15 transition-colors"
        >
          ⚠️ {dbPending.length} migration{dbPending.length > 1 ? 's' : ''} en attente — ouvrir la maintenance →
        </button>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-5 flex-wrap">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => switchTab(t.id)}
            className={['px-4 py-2 rounded-xl text-sm transition-all', tab === t.id ? 'glass-btn-accent' : 'glass-btn'].join(' ')}
          >
            {t.emoji} {t.label}{t.count > 0 ? ` (${t.count})` : ''}
          </button>
        ))}
      </div>

      {loading && <div className="text-text-muted animate-pulse py-10 text-center text-sm">Chargement…</div>}

      {/* ─── Entreprises ──────────────────────────────────────────────────────── */}
      {!loading && tab === 'entreprises' && (
        <div className="glass-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/[0.08] text-text-secondary text-xs uppercase tracking-wider">
                <th className="text-left px-4 py-3 font-medium">Entreprise</th>
                <th className="text-left px-4 py-3 font-medium">Email</th>
                <th className="text-left px-4 py-3 font-medium">SIRET</th>
                <th className="text-left px-4 py-3 font-medium">Téléphone</th>
                <th className="text-center px-4 py-3 font-medium">Missions</th>
                <th className="text-center px-4 py-3 font-medium">Inscrit le</th>
                <th className="text-center px-4 py-3 font-medium">Statut</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {filteredCompanies.length === 0
                ? <tr><td colSpan={8} className="text-center text-text-muted py-10">Aucun résultat</td></tr>
                : filteredCompanies.map(c => (
                  <tr key={c.id} className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors">
                    <td className="px-4 py-3 text-text-primary font-medium">{c.name}</td>
                    <td className="px-4 py-3 text-text-secondary">{c.email}</td>
                    <td className="px-4 py-3 text-text-muted font-mono text-xs">{c.siret ?? '—'}</td>
                    <td className="px-4 py-3 text-text-muted">{c.phone ?? '—'}</td>
                    <td className="px-4 py-3 text-center text-text-secondary">{c._count.missions}</td>
                    <td className="px-4 py-3 text-center text-text-muted text-xs">{formatDate(c.createdAt)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={['text-xs px-2 py-0.5 rounded-full border', c.isSuspended ? 'bg-danger/10 border-danger/25 text-danger' : 'bg-success/10 border-success/25 text-success'].join(' ')}>
                        {c.isSuspended ? 'Suspendu' : 'Actif'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => toggleSuspend('companies', c.id, c.isSuspended)}
                        className={['glass-btn px-3 py-1 text-xs rounded-lg', c.isSuspended ? 'text-success' : 'text-danger'].join(' ')}
                      >
                        {c.isSuspended ? 'Réactiver' : 'Suspendre'}
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ─── Prestataires ─────────────────────────────────────────────────────── */}
      {!loading && tab === 'prestataires' && (
        <div className="glass-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/[0.08] text-text-secondary text-xs uppercase tracking-wider">
                <th className="text-left px-4 py-3 font-medium">Nom</th>
                <th className="text-left px-4 py-3 font-medium">Email</th>
                <th className="text-left px-4 py-3 font-medium">Téléphone</th>
                <th className="text-left px-4 py-3 font-medium">Spécialités</th>
                <th className="text-center px-4 py-3 font-medium">Note</th>
                <th className="text-center px-4 py-3 font-medium">Missions</th>
                <th className="text-center px-4 py-3 font-medium">Inscrit le</th>
                <th className="text-center px-4 py-3 font-medium">Statut</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {filteredProviders.length === 0
                ? <tr><td colSpan={9} className="text-center text-text-muted py-10">Aucun résultat</td></tr>
                : filteredProviders.map(p => (
                  <tr key={p.id} className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors">
                    <td className="px-4 py-3 text-text-primary font-medium">{p.firstName} {p.lastName}</td>
                    <td className="px-4 py-3 text-text-secondary">{p.email}</td>
                    <td className="px-4 py-3 text-text-muted">{p.phone ?? '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {p.specialties?.length > 0
                          ? p.specialties.map(s => (
                            <span key={s} className="text-[10px] px-1.5 py-0.5 rounded-full bg-accent/10 border border-accent/20 text-accent">
                              {specialtyByKey.get(s)?.shortLabel ?? s}
                            </span>
                          ))
                          : <span className="text-text-muted text-xs">—</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center text-xs text-text-secondary">
                      {p.ratingCount > 0 ? `⭐ ${p.rating.toFixed(1)} (${p.ratingCount})` : '—'}
                    </td>
                    <td className="px-4 py-3 text-center text-text-secondary">{p._count.assignments}</td>
                    <td className="px-4 py-3 text-center text-text-muted text-xs">{formatDate(p.createdAt)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={['text-xs px-2 py-0.5 rounded-full border', p.isSuspended ? 'bg-danger/10 border-danger/25 text-danger' : 'bg-success/10 border-success/25 text-success'].join(' ')}>
                        {p.isSuspended ? 'Suspendu' : 'Actif'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => toggleSuspend('providers', p.id, p.isSuspended)}
                        className={['glass-btn px-3 py-1 text-xs rounded-lg', p.isSuspended ? 'text-success' : 'text-danger'].join(' ')}
                      >
                        {p.isSuspended ? 'Réactiver' : 'Suspendre'}
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ─── Paiements ────────────────────────────────────────────────────────── */}
      {!loading && tab === 'paiements' && (
        <div className="glass-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/[0.08] text-text-secondary text-xs uppercase tracking-wider">
                <th className="text-left px-4 py-3 font-medium">Mission</th>
                <th className="text-left px-4 py-3 font-medium">Entreprise</th>
                <th className="text-left px-4 py-3 font-medium">Prestataire</th>
                <th className="text-center px-4 py-3 font-medium">Méthode</th>
                <th className="text-right px-4 py-3 font-medium">Montant</th>
                <th className="text-right px-4 py-3 font-medium">Commission</th>
                <th className="text-right px-4 py-3 font-medium">Net prestataire</th>
                <th className="text-center px-4 py-3 font-medium">Statut</th>
                <th className="text-center px-4 py-3 font-medium">Date</th>
              </tr>
            </thead>
            <tbody>
              {filteredPayments.length === 0
                ? <tr><td colSpan={9} className="text-center text-text-muted py-10">Aucun résultat</td></tr>
                : filteredPayments.map(p => {
                  const cfg = PAYMENT_STATUS[p.status] ?? PAYMENT_STATUS.PENDING
                  const provider = p.mission.assignments[0]?.provider
                  return (
                    <tr key={p.id} className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors">
                      <td className="px-4 py-3 text-text-primary font-medium max-w-[180px] truncate">{p.mission.title}</td>
                      <td className="px-4 py-3 text-text-secondary">{p.mission.company.name}</td>
                      <td className="px-4 py-3 text-text-muted text-xs">
                        {provider ? `${provider.firstName} ${provider.lastName}` : '—'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-white/[0.06] border border-white/[0.12] text-text-secondary">
                          {p.method}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-text-primary font-medium">{euros(p.amount)}</td>
                      <td className="px-4 py-3 text-right text-danger text-xs">{euros(p.platformFee)}</td>
                      <td className="px-4 py-3 text-right text-success text-xs">{euros(p.providerNet)}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={['text-xs px-2 py-0.5 rounded-full border', cfg.color].join(' ')}>
                          {cfg.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center text-text-muted text-xs">
                        {p.capturedAt ? formatDate(p.capturedAt) : formatDate(p.createdAt)}
                      </td>
                    </tr>
                  )
                })}
            </tbody>
          </table>
          {filteredPayments.length > 0 && (
            <div className="px-4 py-3 border-t border-white/[0.06] flex gap-6 text-xs text-text-muted">
              <span>Total encaissé : <span className="text-text-primary font-semibold">
                {euros(filteredPayments.filter(p => ['CAPTURED','RELEASED'].includes(p.status)).reduce((s, p) => s + p.amount, 0))}
              </span></span>
              <span>Commissions : <span className="text-danger font-semibold">
                {euros(filteredPayments.filter(p => ['CAPTURED','RELEASED'].includes(p.status)).reduce((s, p) => s + p.platformFee, 0))}
              </span></span>
              <span>Reversé prestataires : <span className="text-success font-semibold">
                {euros(filteredPayments.filter(p => ['CAPTURED','RELEASED'].includes(p.status)).reduce((s, p) => s + p.providerNet, 0))}
              </span></span>
            </div>
          )}
        </div>
      )}

      {/* ─── Formations ───────────────────────────────────────────────────────── */}
      {!loading && tab === 'formations' && (
        <div className="glass-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/[0.08] text-text-secondary text-xs uppercase tracking-wider">
                <th className="text-left px-4 py-3 font-medium">Intitulé</th>
                <th className="text-left px-4 py-3 font-medium">Entreprise</th>
                <th className="text-center px-4 py-3 font-medium">Type</th>
                <th className="text-center px-4 py-3 font-medium">Durée</th>
                <th className="text-left px-4 py-3 font-medium">Lieu</th>
                <th className="text-center px-4 py-3 font-medium">Stagiaires</th>
                <th className="text-center px-4 py-3 font-medium">Début</th>
                <th className="text-center px-4 py-3 font-medium">Statut</th>
              </tr>
            </thead>
            <tbody>
              {filteredSessions.length === 0
                ? <tr><td colSpan={8} className="text-center text-text-muted py-10">Aucun résultat</td></tr>
                : filteredSessions.map(s => {
                  const cfg = TRAINING_STATUS[s.status] ?? TRAINING_STATUS.PLANNED
                  return (
                    <tr key={s.id} className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors">
                      <td className="px-4 py-3 text-text-primary font-medium max-w-[200px] truncate">{s.title}</td>
                      <td className="px-4 py-3 text-text-secondary">{s.company.name}</td>
                      <td className="px-4 py-3 text-center text-text-muted text-xs">{TRAINING_TYPE[s.type] ?? s.type}</td>
                      <td className="px-4 py-3 text-center text-text-secondary">{s.durationH}h</td>
                      <td className="px-4 py-3 text-text-muted text-xs">{s.isRemote ? 'À distance' : s.city ?? '—'}</td>
                      <td className="px-4 py-3 text-center text-text-secondary">{s._count.trainees}</td>
                      <td className="px-4 py-3 text-center text-text-muted text-xs">{formatDate(s.startDate)}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={['text-xs px-2 py-0.5 rounded-full border', cfg.color].join(' ')}>{cfg.label}</span>
                      </td>
                    </tr>
                  )
                })}
            </tbody>
          </table>
        </div>
      )}

      {/* ─── Avis ─────────────────────────────────────────────────────────────── */}
      {!loading && tab === 'avis' && (
        <div className="glass-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/[0.08] text-text-secondary text-xs uppercase tracking-wider">
                <th className="text-left px-4 py-3 font-medium">Prestataire</th>
                <th className="text-left px-4 py-3 font-medium">Mission</th>
                <th className="text-left px-4 py-3 font-medium">Entreprise</th>
                <th className="text-center px-4 py-3 font-medium">Note globale</th>
                <th className="text-center px-4 py-3 font-medium">Ponctualité</th>
                <th className="text-center px-4 py-3 font-medium">Comportement</th>
                <th className="text-left px-4 py-3 font-medium">Commentaire</th>
                <th className="text-center px-4 py-3 font-medium">Date</th>
              </tr>
            </thead>
            <tbody>
              {filteredReviews.length === 0
                ? <tr><td colSpan={8} className="text-center text-text-muted py-10">Aucun résultat</td></tr>
                : filteredReviews.map(r => (
                  <tr key={r.id} className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors">
                    <td className="px-4 py-3 text-text-primary font-medium">
                      {r.provider.firstName} {r.provider.lastName}
                    </td>
                    <td className="px-4 py-3 text-text-secondary max-w-[160px] truncate">{r.mission.title}</td>
                    <td className="px-4 py-3 text-text-muted">{r.mission.company.name}</td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-warning font-bold">{'★'.repeat(r.rating)}{'☆'.repeat(5 - r.rating)}</span>
                      <span className="text-text-muted text-xs ml-1">{r.rating}/5</span>
                    </td>
                    <td className="px-4 py-3 text-center text-text-secondary text-xs">{r.punctuality}/5</td>
                    <td className="px-4 py-3 text-center text-text-secondary text-xs">{r.behavior}/5</td>
                    <td className="px-4 py-3 text-text-muted text-xs max-w-[200px] truncate">
                      {r.comment ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-center text-text-muted text-xs">{formatDate(r.createdAt)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
          {filteredReviews.length > 0 && (
            <div className="px-4 py-3 border-t border-white/[0.06] text-xs text-text-muted">
              Moyenne générale : <span className="text-warning font-semibold">
                {(filteredReviews.reduce((s, r) => s + r.rating, 0) / filteredReviews.length).toFixed(2)}/5
              </span>
              {' · '}{filteredReviews.length} avis au total
            </div>
          )}
        </div>
      )}

      {/* ─── Maintenance ──────────────────────────────────────────────────────── */}
      {tab === 'maintenance' && <MaintenancePanel onPendingChange={setDbPending} />}
    </div>
  )
}
